import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import { permissionList } from '../../../shared-functions/JWTDocodeFunction';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import { passwordValidation } from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';

/**
 * Initial password reset component
 * @returns {any} The rendered Initial password reset component.
 * @description Initial password reset component is a component for initial password reset functionality.
 */
function InitialPasswordReset(): React.JSX.Element {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const [credentials, setCredentials] = useState<{
    password: string;
    confirmPassword: string;
  }>({
    password: '',
    confirmPassword: '',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param {any} name - The name
   * @param {any} changeValue - The changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
  };

  /**
   * Whether the new-password field should show an error state.
   * @returns {boolean} true when field is empty or fails the password rule
   */
  const hasPasswordError = () =>
    formStatus.showFormErrors && !passwordValidation(credentials.password);

  /**
   * Resolves which message to show under the new-password field: required
   * first, otherwise the format rule.
   * @returns {string} translated error message
   */
  const passwordErrorMessage = () =>
    credentials.password ? t('passwordValidationRequirements') : t('pleaseEnterPassword');

  /**
   * Whether the confirm-password field should show an error state.
   * @returns {boolean} true when field is empty or doesn't match the password
   */
  const hasConfirmPasswordError = () =>
    formStatus.showFormErrors && credentials.confirmPassword !== credentials.password;

  /**
   * Resolves which message to show under the confirm-password field:
   * required first, otherwise the mismatch message.
   * @returns {string} translated error message
   */
  const confirmPasswordErrorMessage = () =>
    credentials.confirmPassword ? t('passwordAndConfirmMustMatch') : t('confirmPasswordRequired');

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      passwordValidation(credentials.password) &&
      credentials.password === credentials.confirmPassword
    ) {
      setFormStatus({
        showFormErrors: true,
        hint: false,
        buttonLoader: true,
      });
      const requestBody = {
        newPassword: credentials.password,
      };
      auth
        .initialResetPassword(requestBody)
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });

          const userVerification = getLocalStorageItem('userVerification');

          let updatedUserVerification;
          if (userVerification?.redirectToEmailVerification) {
            updatedUserVerification = {
              ...userVerification,
              isInitialPasswordReset: true,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/email-verification');
          } else if (userVerification?.redirectToPhoneVerification) {
            updatedUserVerification = {
              ...userVerification,
              isInitialPasswordReset: true,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/phone-number-verification');
          } else {
            removeLocalStorageItem('userVerification'); // Remove user verification from local storage
            if (accountVerificationStatus === 'approved') {
              // Dashboard permission
              const permissions = permissionList();

              if (permissions.includes('dashboard')) {
                navigate('/ev-admin/dashboard');
                return;
              }

              if (permissions.includes('charging_station')) {
                navigate('/ev-admin/operations/charging-station/approved');
              } else if (permissions.includes('device_management')) {
                navigate('/ev-admin/operations/chargers');
              } else if (permissions.includes('reporting_tools')) {
                navigate('/ev-admin/reports/energyReport');
              } else if (permissions.includes('payments')) {
                navigate('/ev-admin/payments');
              } else if (permissions.includes('wallet_revenue_sharing')) {
                navigate('/ev-admin/revenue/sharing');
              } else if (permissions.includes('campaign_management')) {
                navigate('/ev-admin/tools/calculator');
              } else if (permissions.includes('virtualLoadBalancer')) {
                navigate('/ev-admin/vlbc');
              } else if (permissions.includes('schedule_maintenance')) {
                navigate('/ev-admin/maintenance-scheduling');
              } else {
                navigate('/ev-admin/profile');
              }
            } else {
              navigate('../ev-admin/profile');
            }
          }
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          // Display a user-friendly message
          dispatch(
            setNotificationMessage({
              type: 'error',
              status: true,
              code: error?.response?.data?.code,
              message: error?.response
                ? error?.response?.data?.message || t('somethingWentWrong')
                : t('noInternetConnection'),
            }),
          );
        });
    }
  };

  return (
    <div className="login-component-v2" dir={i18n.dir()}>
      <div className="login-component-v2-body">
        <CustomInput
          inputLabel={t('createNewPassword')}
          type="password"
          placeholder={t('enterPassword')}
          name="password"
          value={credentials.password}
          onChangeValue={handleCredentialChange}
          error={hasPasswordError()}
          errorMessage={passwordErrorMessage()}
          hint={
            credentials.password.length === 0 || !passwordValidation(credentials.password)
              ? formStatus.hint
              : false
          }
          hintMessage={t('passwordMustBeAtLeast12Char')}
        />
        <CustomInput
          inputLabel={t('confirmPassword')}
          type="password"
          placeholder={t('enterPassword')}
          name="confirmPassword"
          value={credentials.confirmPassword}
          onChangeValue={handleCredentialChange}
          error={hasConfirmPasswordError()}
          errorMessage={confirmPasswordErrorMessage()}
        />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={handleSubmit}
          loading={formStatus.buttonLoader}
          disableButton={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

export default InitialPasswordReset;
